/**
 * Pages that stand ALONE, outside the CRM: the live board (`/live/<kf-id>`,
 * features/Dashboard/v2/liveBoard). Such a page is opened from a copied link
 * in its own tab, so it reads the address once.
 *
 *  - It never opens the socket, which is what feeds the CRM's own popups and
 *    toasts ("New lead for you", stage tones): a board left on a screen must
 *    not ring with them (owner, 2026-10-28: "completely detached from the crm").
 *  - It has its OWN sign-in (owner, 2026-10-28: "we need separate
 *    authentication for the link based view ... it asks for password if i
 *    logged in"): every request from it carries the board's token, never the
 *    CRM's (lib/axios.tsx), and the CRM never sees the board's. The token sits
 *    in sessionStorage — one tab: a refresh stays unlocked, but the link
 *    pasted into any other tab or browser asks for the password again, even
 *    where the CRM is signed in. Signing in or out here never touches the CRM.
 */
const DETACHED_PREFIXES = ["/live/"];

export function isDetachedPage(pathname: string = window.location.pathname): boolean {
  return DETACHED_PREFIXES.some((p) => pathname.startsWith(p));
}

const TOKEN_KEY = "xale_live_board_token";
const USER_KEY = "xale_live_board_user";

/** The signed-in person as `/auth/verify` returns them (name, tenant, …). */
export type DetachedUser = Record<string, any>;

export interface DetachedSession {
  token: string | null;
  user: DetachedUser | null;
}

function read(): DetachedSession {
  try {
    const token = sessionStorage.getItem(TOKEN_KEY);
    const raw = sessionStorage.getItem(USER_KEY);
    return { token, user: token && raw ? (JSON.parse(raw) as DetachedUser) : null };
  } catch {
    // Storage blocked or unreadable: the page simply stays locked.
    return { token: null, user: null };
  }
}

let snapshot: DetachedSession = read();
const listeners = new Set<() => void>();

function write(next: DetachedSession) {
  try {
    if (next.token) sessionStorage.setItem(TOKEN_KEY, next.token);
    else sessionStorage.removeItem(TOKEN_KEY);
    if (next.token && next.user) sessionStorage.setItem(USER_KEY, JSON.stringify(next.user));
    else sessionStorage.removeItem(USER_KEY);
  } catch {
    // Kept in memory for this page view only.
  }
  snapshot = next;
  listeners.forEach((l) => l());
}

/** The board's token for lib/axios.tsx — null while the board is locked. */
export const getDetachedToken = (): string | null => snapshot.token;

export const getDetachedSession = (): DetachedSession => snapshot;

export const subscribeDetachedSession = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const setDetachedSession = (token: string, user: DetachedUser | null) => write({ token, user });

export const setDetachedUser = (user: DetachedUser) => {
  if (snapshot.token) write({ token: snapshot.token, user });
};

export const clearDetachedSession = () => write({ token: null, user: null });
